import React, { useEffect, useState } from 'react';
import { FiKey, FiCheck, FiX, FiCopy, FiEye, FiEyeOff, FiRefreshCw, FiPlus, FiTerminal } from 'react-icons/fi';
import '../../styles/MarketplacePage.css';
import '../../styles/Webhooks.css';

// Both the secret-key management calls and the inbound webhooks live under the
// same MyAMP prefix (routes/insurance_agency_webhooks.py, mounted at
// /api/insurance-agency in main.py).
const WEBHOOK_BASE_URL = `${process.env.REACT_APP_MyAMP_Url}/api/insurance-agency`;
const SECRET_API_URL = `${WEBHOOK_BASE_URL}/webhook-secret`;

const formatDate = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? iso
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

interface DocEndpoint {
    id: string;
    title: string;
    description: string;
    method: string;
    url: string;
    body: string;
    /** Success status the endpoint answers with, e.g. "200 OK" / "202 Accepted". */
    responseStatus: string;
    response: string;
    notes: string[];
    errors: string[];
}

const DOCS: DocEndpoint[] = [
    {
        id: 'command',
        title: 'Create a Command',
        description: 'Creates a command in Momentum Command, assigns it to a Digital Agent and starts the agent run in the background.',
        method: 'POST',
        url: `${WEBHOOK_BASE_URL}/webhooks/command`,
        body: `{
  "title": "Send COI to ABC Corp",
  "description": "Issue a certificate of insurance for ABC Corp and email it to the client.",
  "categoryName": "COI Request",
  "assignee": "<Digital Agent name>",
  "status": 1,
  "transactionId": "1829afb1-b8f2-4f14-8a28-1bd45e8e85af",
  "fileUrls": [
    "https://example.com/files/abc-corp-policy.pdf"
  ]
}`,
        responseStatus: '200 OK',
        response: `{
  "taskId": 4821,
  "taskNumber": "CMD-1042",
  "transactionId": "1829afb1-b8f2-4f14-8a28-1bd45e8e85af"
}`,
        notes: [
            'title — required. The command title.',
            'description — required. What the Digital Agent should do; this is the instruction the run executes.',
            'categoryName — required. Command category matched by name (case-insensitive): built-in categories first, then your custom SOP categories.',
            "assignee — optional. Name of one of your Digital Agents that runs the command. When omitted, the account's primary Digital Agent is used.",
            'status — optional. 0 = To Do, 1 = In Progress. When omitted, the command starts In Progress because it is agent-assigned.',
            "transactionId — optional. Dashed GUID (8-4-4-4-12) used as the folder key for the command's documents. Momentum AMP generates one if it's not passed. If fileUrls are sent and the GUID already belongs to another command, a fresh one is used. The GUID in effect is returned in the response.",
            'fileUrls — optional, default []. Publicly reachable http(s) URLs of files to attach. Each is downloaded and stored with the command. Duplicate file names within one call get a _2 / _3 suffix.',
        ],
        errors: [
            '400 — missing or invalid field, unknown category, unknown Digital Agent, or a fileUrls entry that cannot be downloaded. No command is created.',
            '401 — missing or invalid x-api-key.',
            '502 — a file upload or the command creation failed. No command is created.',
        ],
    },
    {
        id: 'routine',
        title: 'Trigger a Routine',
        description: 'Runs an existing Momentum Routine by name. Returns as soon as the run is accepted; the routine executes in the background.',
        method: 'POST',
        url: `${WEBHOOK_BASE_URL}/webhook/routine`,
        body: `{
  "name": "Daily renewal follow-ups"
}`,
        responseStatus: '202 Accepted',
        response: `{
  "routineId": "3f2c9a4e-1b7d-4c0e-9a8f-2d6b5e7c1a90",
  "runId": "8c1e4f2a-6b3d-4e9f-a1c7-0d5b2e8f9a34",
  "chatId": "b7d2e9c1-4a6f-4d3e-8b1a-9f0c2e5d7a61",
  "name": "Daily renewal follow-ups",
  "accepted": true
}`,
        notes: [
            'name — required. Exact name (case-insensitive) of an active routine in Momentum Routines that belongs to your account. The name must be unique across your routines.',
            'runId / chatId — identify the run that was started; use them to look the run up in Routines.',
        ],
        errors: [
            '400 — name is missing.',
            '401 — missing or invalid x-api-key.',
            '404 — no routine with that name.',
            '409 — the routine is inactive (returns "skipped": true) or more than one routine shares the name.',
        ],
    },
];

// Secret-key state loaded from the API. The secret itself is returned ONLY by
// create/regenerate — after that the backend only reports whether one exists,
// so `secret` is non-null just for the current page session right after
// creating/regenerating (the "copy it now" window).
interface KeyState {
    exists: boolean;
    createdAt: string | null;
    secret: string | null;
}

const WebhooksPage: React.FC = () => {
    const [keyState, setKeyState] = useState<KeyState | null>(null); // null = still loading
    const [revealKey, setRevealKey] = useState(false);
    const [busy, setBusy] = useState(false);
    const [regenerateConfirm, setRegenerateConfirm] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 2500);
    };

    const copy = (value: string, label: string) => {
        navigator.clipboard.writeText(value).then(() => showToast(`${label} copied to clipboard.`)).catch(() => { });
    };

    const authHeaders = (): Record<string, string> => ({
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    });

    // On mount: ask the backend whether a key exists (the key itself is never
    // returned here — only { exists, created_at }).
    useEffect(() => {
        let cancelled = false;
        fetch(SECRET_API_URL, { headers: authHeaders() })
            .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
            .then(data => {
                if (cancelled) return;
                setKeyState({
                    exists: !!data?.exists,
                    createdAt: data?.created_at || null,
                    secret: null,
                });
            })
            .catch(() => {
                if (cancelled) return;
                setKeyState({ exists: false, createdAt: null, secret: null });
                showToast('Failed to load secret key status.', 'error');
            });
        return () => { cancelled = true; };
    }, []);

    const handleCreateKey = () => {
        setBusy(true);
        fetch(SECRET_API_URL, { method: 'POST', headers: authHeaders() })
            .then(res => res.json().then(data => ({ ok: res.ok, status: res.status, data })))
            .then(({ ok, status, data }) => {
                if (ok) {
                    setKeyState({ exists: true, createdAt: data.created_at || null, secret: data.secret_key });
                    setRevealKey(true);
                    showToast('Secret key created.');
                } else if (status === 409) {
                    // A key already exists (stale page state) — flip to the exists view.
                    setKeyState(prev => ({ exists: true, createdAt: prev?.createdAt || null, secret: null }));
                    showToast('A secret key already exists. Use regenerate to rotate it.', 'error');
                } else {
                    showToast(data?.error || 'Failed to create secret key.', 'error');
                }
            })
            .catch(() => showToast('Failed to create secret key.', 'error'))
            .finally(() => setBusy(false));
    };

    const handleRegenerate = () => {
        setRegenerateConfirm(false);
        setBusy(true);
        fetch(`${SECRET_API_URL}/regenerate`, { method: 'POST', headers: authHeaders() })
            .then(res => res.json().then(data => ({ ok: res.ok, data })))
            .then(({ ok, data }) => {
                if (ok) {
                    setKeyState({ exists: true, createdAt: data.created_at || null, secret: data.secret_key });
                    setRevealKey(true);
                    showToast('Secret key regenerated. Update any integrations using the old key.');
                } else {
                    showToast(data?.error || 'Failed to regenerate secret key.', 'error');
                }
            })
            .catch(() => showToast('Failed to regenerate secret key.', 'error'))
            .finally(() => setBusy(false));
    };

    const secret = keyState?.secret || null;
    const keyHeaderValue = secret && revealKey ? secret : '<YOUR_SECRET_KEY>';

    const toastColor = toast?.type === 'success' ? '#10b981' : '#ef4444';

    return (
        <div style={{ padding: '24px 32px', height: '100%', overflowY: 'auto' }}>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 20, zIndex: 10001,
                    background: `${toastColor}18`,
                    border: `1px solid ${toastColor}44`,
                    borderRadius: 10, padding: '10px 16px',
                    color: toastColor, fontSize: 13, fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}>
                    {toast.type === 'success' ? <FiCheck size={14} /> : <FiX size={14} />}
                    {toast.msg}
                </div>
            )}

            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: 680, marginBottom: 20 }}>
                Create a secret key that external systems can use to create commands or trigger routines in Momentum AMP.
            </div>

            {/* ── Secret Key card ── */}
            <div className="wh-key-card">
                <div className="wh-key-card__icon">
                    <FiKey size={20} />
                </div>
                <div className="wh-key-card__body">
                    <div className="wh-key-card__title">Secret Key</div>
                    {keyState === null ? (
                        <div className="wh-key-card__sub">Loading…</div>
                    ) : !keyState.exists ? (
                        <div className="wh-key-card__sub">No secret key has been created yet.</div>
                    ) : (
                        <div className="wh-key-card__sub">
                            {keyState.createdAt ? `Created ${formatDate(keyState.createdAt)}` : 'Active'}
                        </div>
                    )}

                    {keyState?.exists && (
                        <>
                            {secret ? (
                                <div className="wh-created-banner" style={{ marginTop: 10 }}>
                                    <FiCheck size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                                    <span>Copy this key now — it won't be shown again. Send it as the <code>x-api-key</code> header shown in the documentation below.</span>
                                </div>
                            ) : (
                                <div className="wh-key-card__sub" style={{ marginTop: 10 }}>
                                    For security, the key is only shown once at creation. If you've lost it, regenerate a new one.
                                </div>
                            )}
                            <div className="wh-field__row" style={{ marginTop: 12 }}>
                                <input
                                    className="wh-field__input wh-field__input--mono"
                                    readOnly
                                    value={secret && revealKey ? secret : '•'.repeat(32)}
                                    onClick={e => (e.target as HTMLInputElement).select()}
                                />
                                {secret && (
                                    <>
                                        <button className="wh-icon-btn" onClick={() => setRevealKey(r => !r)} title={revealKey ? 'Hide key' : 'View key'}>
                                            {revealKey ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                                        </button>
                                        <button className="wh-icon-btn" onClick={() => copy(secret, 'Secret key')} title="Copy key">
                                            <FiCopy size={14} />
                                        </button>
                                    </>
                                )}
                                <button className="wh-icon-btn" disabled={busy} onClick={() => setRegenerateConfirm(true)} title="Regenerate key">
                                    <FiRefreshCw size={14} />
                                </button>
                            </div>
                        </>
                    )}
                </div>
                {keyState !== null && !keyState.exists && (
                    <button className="wh-btn-save" style={{ flexShrink: 0 }} disabled={busy} onClick={handleCreateKey}>
                        <FiPlus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> {busy ? 'Creating…' : 'Create Key'}
                    </button>
                )}
            </div>

            {/* ── Documentation ── */}
            <div className="wh-doc-heading">
                <FiTerminal size={13} />
                Documentation
            </div>

            {DOCS.map(doc => (
                <div key={doc.id} className="wh-doc-card">
                    <div className="wh-doc-card__title">{doc.title}</div>
                    <div className="wh-doc-card__desc">{doc.description}</div>

                    <div className="wh-doc-row">
                        <span className="wh-doc-method">{doc.method}</span>
                        <code className="wh-doc-url">{doc.url}</code>
                        <button className="wh-icon-btn" style={{ width: 30, height: 30 }} onClick={() => copy(doc.url, 'URL')} title="Copy URL">
                            <FiCopy size={12} />
                        </button>
                    </div>

                    <div className="wh-doc-label">Headers</div>
                    <div className="wh-doc-row">
                        <code className="wh-doc-url">x-api-key: {keyHeaderValue}</code>
                        {secret && (
                            <button className="wh-icon-btn" style={{ width: 30, height: 30 }} onClick={() => copy(secret, 'Secret key')} title="Copy key">
                                <FiCopy size={12} />
                            </button>
                        )}
                    </div>

                    <div className="wh-doc-label">Body</div>
                    <div className="wh-code-block-wrap">
                        <pre className="wh-code-block">{doc.body}</pre>
                        <button className="wh-icon-btn wh-code-block-copy" onClick={() => copy(doc.body, 'Request body')} title="Copy body">
                            <FiCopy size={12} />
                        </button>
                    </div>

                    {doc.notes.length > 0 && (
                        <ul className="wh-doc-notes">
                            {doc.notes.map((note, i) => <li key={i}>{note}</li>)}
                        </ul>
                    )}

                    <div className="wh-doc-label">Response · {doc.responseStatus}</div>
                    <div className="wh-code-block-wrap">
                        <pre className="wh-code-block">{doc.response}</pre>
                        <button className="wh-icon-btn wh-code-block-copy" onClick={() => copy(doc.response, 'Response')} title="Copy response">
                            <FiCopy size={12} />
                        </button>
                    </div>

                    {doc.errors.length > 0 && (
                        <>
                            <div className="wh-doc-label">Errors</div>
                            <ul className="wh-doc-notes">
                                {doc.errors.map((err, i) => <li key={i}>{err}</li>)}
                            </ul>
                        </>
                    )}
                </div>
            ))}

            {regenerateConfirm && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setRegenerateConfirm(false)}>
                    <div className="mcp-delete-dialog" onClick={e => e.stopPropagation()}>
                        <h3 className="mcp-delete-dialog__title">Regenerate Secret Key?</h3>
                        <p className="mcp-delete-dialog__body">
                            The current key will stop working immediately. Any system calling the Command or Routine URLs with the old key will start getting rejected until it's updated with the new one.
                        </p>
                        <div className="mcp-delete-dialog__actions">
                            <button className="mcp-delete-dialog__cancel" onClick={() => setRegenerateConfirm(false)}>Cancel</button>
                            <button className="mcp-delete-dialog__confirm" onClick={handleRegenerate}>Regenerate</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebhooksPage;
